import {
    Flex,
    Text,
    Divider,
    VStack,
    Link,
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { AlertCircleIcon, CheckCircleIcon2, CloseCircleIcon } from "@/styles/icons";
import { FileUpload } from "@/components";
import { usePayment } from "@/hooks/usePayment";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { formatCurrency } from "@/utils";
import { FiChevronDown } from "react-icons/fi";
import { Loading, Tooltip, Input, Drawer } from "barte-design-system";

interface DrawerNewPaymentBatchProps {
    isOpen: boolean;
    onClose: () => void;
}

export function DrawerNewPaymentBatch({
    isOpen,
    onClose,
}: DrawerNewPaymentBatchProps) {
    const [section, setSection] = useState<"PAYMENT_FLOW" | "ERROR" | "SUCCESS">("PAYMENT_FLOW");
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [batchName, setBatchName] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [batchResponse, setBatchResponse] = useState<any>(null);
    const [showDetailsParsingErrors, setShowDetailsParsingErrors] = useState(false);
    const [showDetailsFailedPayments, setShowDetailsFailedPayments] = useState(false);
    const itemsPerPage = 5;
    const sortDirection = 'DESC';
    const sortField = 'createdAt';
    const pageable = {
        page: 0,
        size: itemsPerPage,
        sort: `${sortField},${sortDirection}`,
    };
    const { createPaymentBatch, submitPaymentBatch, getListPaymentsBatchesInitialData } = usePayment();
    const { execute: executeCreatePaymentBatch, loading: loadingCreateBatch } = useAsyncFunction(
        async (fileName: string, file: File) => {
            return await createPaymentBatch(fileName, file);
        }
    );
    const { execute: executeSubmitPaymentBatch, loading: loadingSubmitBatch } = useAsyncFunction(
        async (batchId: number) => {
            return await submitPaymentBatch(batchId);
        }
    );

    // Mapa de steps - será definido conforme necessário
    const STEPS = ["UPLOAD_FILES", "PROCESSING", "REVIEW"];

    const handleClearInputs = () => {
        setBatchName("");
        setSelectedFiles([]);
        setBatchResponse(null);
    };

    const handleDownloadTemplate = () => { window.open("https://docs.google.com/spreadsheets/d/1v4flucJczv9_erfcBNtC4UfCgvUB1ssB9OHOKvoLP8k/edit?gid=380586713#gid=380586713", "_blank") };

    const handleCloseDrawer = () => {
        onClose();
        if(section === "SUCCESS") {
            getListPaymentsBatchesInitialData(pageable);
        }
        handleClearInputs();
        setCurrentStepIndex(0);
        setSection("PAYMENT_FLOW");
    };

    const handleNextStep = async () => {
        // Validar se há arquivos e nome do lote antes de avançar
        if (currentStepIndex === 0) {
            if (!batchName.trim() || selectedFiles.length === 0) {
                return;
            }
            // Avança para o step de processamento e inicia o upload
            setCurrentStepIndex(1);
            await handleProcessBatch();
            return;
        }
        
        if (currentStepIndex < STEPS.length - 1) {
            setCurrentStepIndex((prev) => prev + 1);
        }
    };

    const handleProcessBatch = async () => {
        if (selectedFiles.length === 0) return;

        try {
            // Processa o primeiro arquivo (ou todos se necessário)
            const file = selectedFiles[0];
            const response = await executeCreatePaymentBatch(
                batchName,
                file,
            );
            setBatchResponse(response?.data);
            // Após processar, avança para o step de review
            setCurrentStepIndex(2);
        } catch (error) {
            setSection("ERROR");
        }
    };

    const handleBack = () => {
        // Não permite voltar durante o processamento
        if (currentStepIndex === 1 && loadingCreateBatch) {
            return;
        }
        // Se estiver no step de REVIEW, volta direto para UPLOAD_FILES
        if (currentStepIndex === 2) {
            setCurrentStepIndex(0);
            return;
        }
        if (currentStepIndex > 0) {
            setCurrentStepIndex((prev) => prev - 1);
        } else {
            handleCloseDrawer();
        }
    };

    const handleSubmit = async () => {
        try {
            await executeSubmitPaymentBatch(batchResponse.idBatch);
            setSection("SUCCESS");
        } catch (error) {
            setSection("ERROR");
        } finally {
            handleClearInputs();
        }
    };

    useEffect(() => {
        if (isOpen) {
            setSection("PAYMENT_FLOW");
            setCurrentStepIndex(0);
        }
    }, [isOpen]);

    return (
        <Drawer
            title="Novo pagamento em lote"
            isOpen={isOpen}
            onClose={handleCloseDrawer}
            footer={
                section === "SUCCESS" || section === "ERROR"
                    ? { primaryButton: { children: "Fechar", variant: "outline", onClick: handleCloseDrawer } }
                    : {
                        secondaryButton: {
                            children: "Voltar",
                            variant: "outline",
                            onClick: handleBack,
                            disabled: currentStepIndex === 1 && loadingCreateBatch,
                        },
                        primaryButton: {
                            children: currentStepIndex === STEPS.length - 1 ? "Finalizar" : "Continuar",
                            onClick: currentStepIndex === STEPS.length - 1 ? handleSubmit : handleNextStep,
                            disabled:
                                (currentStepIndex === 0 && (!batchName.trim() || selectedFiles.length === 0)) ||
                                loadingCreateBatch ||
                                currentStepIndex === 1,
                            loading: (loadingCreateBatch && currentStepIndex === 1) || loadingSubmitBatch,
                        },
                    }
            }
        >
                    <Flex
                        direction="column"
                        bg="white"
                        borderRadius="lg"
                        w="100%"
                        h="100%"
                    >
                        {section === "PAYMENT_FLOW" && (
                            <Flex
                                direction="column"
                                gap={4}
                                h="100%"
                                w="100%"
                                overflowY="auto"
                                justify="center"
                                align="center"
                            >
                                {STEPS[currentStepIndex] === "UPLOAD_FILES" && (
                                    <VStack align="flex-start" spacing={6} w="100%" p={6}>
                                        {/* Título */}
                                        <Flex flexDir="column" gap={2}>
                                            <Text
                                                fontSize="22px"
                                                fontWeight="400"
                                                color="#000"
                                            >
                                                Faça upload dos arquivos de pagamento
                                            </Text>
                                            <Flex flexDir="column" gap={1}>
                                                <Text fontSize="12px" color="#000">
                                                    Você precisa utilizar o template de cadastro de arquivos.
                                                </Text>
                                                <Text fontSize="12px" color="#000">
                                                    <Link
                                                        color="#CB2159"
                                                        textDecoration="underline"
                                                        onClick={handleDownloadTemplate}
                                                        cursor="pointer"
                                                        fontSize="12px"
                                                    >
                                                        Clique aqui
                                                    </Link>{" "}
                                                    para fazer download do template.
                                                </Text>
                                            </Flex>
                                        </Flex>

                                        {/* Campo Nome do Lote */}
                                        <Flex flexDir="column" gap={2} w="100%">
                                            <Text fontSize="14px" color="#000" fontWeight="500">
                                                Nome do lote
                                            </Text>
                                            <Input
                                                placeholder="Nome"
                                                value={batchName}
                                                onChange={(e) => setBatchName(e.target.value)}
                                          
                                            />
                                        </Flex>

                                        {/* Área de Upload */}
                                        <FileUpload
                                            selectedFiles={selectedFiles}
                                            onFilesChange={setSelectedFiles}
                                            maxSize={5}
                                            acceptedFormats={[".csv", ".xlsx"]}
                                            multiple={false}
                                        />
                                    </VStack>
                                )}

                                {/* Step de Processamento */}
                                {STEPS[currentStepIndex] === "PROCESSING" && (
                                    <Flex
                                        direction="column"
                                        gap={4}
                                        h="100%"
                                        w="100%"
                                        justify="center"
                                        align="center"
                                    >
                                        <Loading type="page" description="Enviando seu lote..." />
                                        <Text fontSize="14px" color="#8A94A8" textAlign="center">
                                            Aguarde até que as informações carreguem na tela.
                                        </Text>
                                    </Flex>
                                )}

                                {/* Step de Revisão */}
                                {STEPS[currentStepIndex] === "REVIEW" && batchResponse && (
                                    <Flex
                                        direction="column"
                                        h="100%"
                                        w="100%"
                                        justify="center"
                                    >
                                        <VStack spacing={0} overflowY="auto">
                                            {/* Banner de Erro Expandable */}
                                            {(() => {
                                                const failedPayments = batchResponse.payments?.filter((p: any) => p.status === "FAILED") || [];
                                                const hasErrors = batchResponse.errors && batchResponse.errors.length > 0;
                                                const parsingErrorsCount = batchResponse.errors.length;
                                                const failedPaymentsCount = failedPayments.length;
                                                
                                                if (parsingErrorsCount > 0 || failedPaymentsCount > 0) {
                                                    return (
                                                        <>
                                                            {parsingErrorsCount > 0 && (
                                                                <Box
                                                                    w="100%"
                                                                    bg="#FBE8E9"
                                                                    p={4}
                                                                    border="1px solid #C62828"
                                                                    borderRadius="4px"
                                                                    mb={2}
                                                                >
                                                                    <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setShowDetailsParsingErrors((prev) => !prev)}>
                                                                        <Flex align="center" gap={3}>
                                                                            <AlertCircleIcon />
                                                                            <Flex flexDir="column" gap={1}>
                                                                                <Text fontSize="14px" fontWeight="600">
                                                                                    O arquivo enviado possui <strong style={{fontSize:"14px",textDecoration: "underline"}}>{parsingErrorsCount} erro{parsingErrorsCount > 1 ? 's' : ''}.</strong>
                                                                                </Text>
                                                                                <Text fontSize="12px" fontWeight="400">
                                                                                    Verifique o arquivo novamente antes de reenviar.
                                                                                </Text>
                                                                            </Flex>
                                                                        </Flex>
                                                                        <FiChevronDown 
                                                                            color="#000" 
                                                                            size={20} 
                                                                            style={{
                                                                                transition: 'transform 0.2s',
                                                                                transform: showDetailsParsingErrors ? 'rotate(180deg)' : 'rotate(0deg)'
                                                                            }}
                                                                        />
                                                                    </Flex>
                                                                    {showDetailsParsingErrors && (
                                                                        <Box mt={4} pl={8}>
                                                                            <VStack align="start" spacing={2} maxH="200px" overflowY="auto">
                                                                                {hasErrors ? (
                                                                                    batchResponse.errors.map((error: any, index: number) => (
                                                                                        <Flex key={index} flexDir="column" gap={1}>
                                                                                            <Text fontSize="12px" color="#C53030" fontWeight="500">
                                                                                                {error.errorMessage}
                                                                                            </Text>
                                                                                        </Flex>
                                                                                    ))
                                                                                ) : null}
                                                                            </VStack>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            )}
                                                            {failedPaymentsCount > 0 && (
                                                                <Box
                                                                    w="100%"
                                                                    bg="#FBE8E9"
                                                                    p={4}
                                                                    border="1px solid #C62828"
                                                                    borderRadius="4px"
                                                                    mb={2}
                                                                >
                                                                    <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setShowDetailsFailedPayments((prev) => !prev)}>
                                                                        <Flex align="center" gap={3}>
                                                                            <AlertCircleIcon />
                                                                            <Flex flexDir="column" gap={1}>
                                                                                <Text fontSize="14px" fontWeight="600">
                                                                                    O arquivo enviado possui <strong style={{fontSize:"14px",textDecoration: "underline"}}>{failedPaymentsCount} erro{failedPaymentsCount > 1 ? 's' : ''} no pagamento.</strong>
                                                                                </Text>
                                                                                <Text fontSize="12px" fontWeight="400">
                                                                                    Verifique o arquivo novamente antes de reenviar.
                                                                                </Text>
                                                                            </Flex>
                                                                        </Flex>
                                                                        <FiChevronDown 
                                                                            color="#000" 
                                                                            size={20} 
                                                                            style={{
                                                                                transition: 'transform 0.2s',
                                                                                transform: showDetailsFailedPayments ? 'rotate(180deg)' : 'rotate(0deg)'
                                                                            }}
                                                                        />
                                                                    </Flex>
                                                                    {showDetailsFailedPayments && (
                                                                        <Box mt={4} pl={8}>
                                                                            <VStack align="start" spacing={2} maxH="200px" overflowY="auto">
                                                                                {
                                                                                    failedPayments.length > 0 && (
                                                                                        <Flex flexDir="column" gap={1}>
                                                                                            <Text fontSize="12px" color="#C53030" fontWeight="500">
                                                                                                Erro no pagamento: valide os dados do arquivo novamente.
                                                                                            </Text>
                                                                                        </Flex>
                                                                                    )
                                                                                }
                                                                            </VStack>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            {/* Banner Informativo */}
                                            <Box
                                                w="100%"
                                                p={4}
                                                bg="#e1f6fd"
                                                border="1px solid #00749e"
                                                borderRadius="4px"
                                                mb={2}
                                            >
                                                <Flex align="center" gap={3}>
                                                    <Text fontSize="14px" color="#000" fontWeight="400">
                                                        Os pagamentos ao serem processados vão para a aba de aprovação na listagem de pagamentos.
                                                    </Text>
                                                </Flex>
                                            </Box>

                                            <VStack align="flex-start" spacing={4} w="100%" p={2}>
                                                {/* Detalhes do Lote */}
                                                <VStack 
                                                    align="stretch" 
                                                    spacing={2} 
                                                    w="100%"
                                                    border="1px solid #E6E9F0" 
                                                    borderRadius="8px" 
                                                >
                                                    {/* Nome do arquivo */}
                                                    <Flex 
                                                        w="100%"
                                                        borderBottom="1px solid #E6E9F0"
                                                    >
                                                        <Flex
                                                            w="100%"
                                                            justify="space-between" 
                                                            align="center" 
                                                            p={2} 
                                                        >
                                                                <Text fontSize="14px" color="#000" fontWeight="400">
                                                                    Nome do arquivo
                                                                </Text>
                                                                <Tooltip
                                                                    label={batchResponse.fileName || selectedFiles[0]?.name}
                                                                    placement="top"
                                                                    hasArrow
                                                                >
                                                                    <Text maxW="180px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" fontSize="14px" color="#000" fontWeight="600">
                                                                        {batchResponse.fileName || selectedFiles[0]?.name}
                                                                    </Text>
                                                                </Tooltip>
                                                        </Flex>
                                                    </Flex>

                                                    {/* Nome do lote */}
                                                    <Flex 
                                                        w="100%"
                                                        borderBottom="1px solid #E6E9F0"
                                                    >
                                                        <Flex
                                                            w="100%"
                                                            justify="space-between" 
                                                            align="center" 
                                                            p={2} 
                                                        >
                                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                                Nome do lote
                                                            </Text>
                                                            <Text maxW="180px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" fontSize="14px" color="#000" fontWeight="500">
                                                                {batchResponse.batchName || batchName}
                                                            </Text>
                                                        </Flex>
                                                    </Flex>

                                                    {/* Itens arquivo original */}
                                                    <Flex 
                                                        w="100%"
                                                        borderBottom="1px solid #E6E9F0"
                                                    >
                                                        <Flex
                                                            w="100%"
                                                            justify="space-between" 
                                                            align="center" 
                                                            p={2} 
                                                        >
                                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                                Itens arquivo original
                                                            </Text>
                                                            <Text fontSize="14px" color="#000" fontWeight="600">
                                                                {batchResponse.numberOfPayments || 0}
                                                            </Text>
                                                        </Flex>
                                                    </Flex>

                                                    {/* Itens processados */}
                                                    <Flex 
                                                        w="100%"
                                                        borderBottom="1px solid #E6E9F0"
                                                    >
                                                        <Flex
                                                            w="100%"
                                                            justify="space-between" 
                                                            align="center" 
                                                            p={2} 
                                                        >
                                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                                IItens sendo processados
                                                            </Text>
                                                            <Text fontSize="14px" color="#000" fontWeight="600">
                                                                {batchResponse.numberOfSuccessfulPayments || 0}
                                                            </Text>
                                                        </Flex>
                                                    </Flex>
                                                </VStack>

                                                <Divider />

                                                {/* Tabela de Pagamentos */}
                                                {batchResponse.payments && batchResponse.payments.length > 0 && (
                                                    <Box w="100%" overflowX="auto">
                                                        <Table variant="simple" size="sm">
                                                            <Thead>
                                                                <Tr>
                                                                    <Th fontSize="12px" color="#8A94A8" fontWeight="400" textTransform="none" px={2}>
                                                                        Conta
                                                                    </Th>
                                                                    <Th fontSize="12px" color="#8A94A8" fontWeight="400" textTransform="none" px={2}>
                                                                        Pagamento
                                                                    </Th>
                                                                    <Th fontSize="12px" color="#8A94A8" fontWeight="400" textTransform="none" px={2} isNumeric>
                                                                        Valor
                                                                    </Th>
                                                                    <Th width="40px" px={2}></Th>
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {batchResponse.payments
                                                                    .filter((payment: any) => payment.status !== "FAILED")
                                                                    .map((payment: any, index: number) => {
                                                                        const getAccountLabel = () => {
                                                                            if (payment.transactionType === "PIX") {
                                                                                return payment.pixKey || "Chave Pix";
                                                                            } else if (payment.transactionType === "BANK_SLIP") {
                                                                                return payment.bankSlipKey || "Boleto";
                                                                            } else if (payment.transactionType === "COPY_PASTE_PIX") {
                                                                                return "Chave Pix";
                                                                            }
                                                                            return "—";
                                                                        };

                                                                        const formatPixKey = (key: string) => {
                                                                            if (!key) return "";
                                                                            if (key.startsWith("+55")) {
                                                                                const numeric = key.replace(/\D/g, "");
                                                                                if (numeric.length === 13) {
                                                                                    return `(${numeric.substring(2, 4)}) ${numeric.substring(4, 9)}-${numeric.substring(9)}`;
                                                                                }
                                                                            }
                                                                            if (key.length > 20) {
                                                                                return `${key.substring(0, 20)}...`;
                                                                            }
                                                                            return key;
                                                                        };

                                                                        const formatBankSlipKey = (key: string) => {
                                                                            if (!key) return "";
                                                                            if (key.length > 20) {
                                                                                return `${key.substring(0, 20)}...`;
                                                                            }
                                                                            return key;
                                                                        };

                                                                        const displayKey = () => {
                                                                            if (payment.transactionType === "PIX" && payment.pixKey) {
                                                                                return formatPixKey(payment.pixKey);
                                                                            } else if (payment.transactionType === "BANK_SLIP" && payment.bankSlipKey) {
                                                                                return formatBankSlipKey(payment.bankSlipKey);
                                                                            }
                                                                            return getAccountLabel();
                                                                        };

                                                                        return (
                                                                            <Tr key={index}>
                                                                                <Td px={2} py={3}>
                                                                                    <VStack align="flex-start" spacing={0}>
                                                                                        <Text fontSize="14px" color="#000" fontWeight="400">
                                                                                            {displayKey()}
                                                                                        </Text>
                                                                                        {payment.costCenter && (
                                                                                            <Text fontSize="12px" color="#8A94A8" fontWeight="400">
                                                                                                {payment.costCenter}
                                                                                            </Text>
                                                                                        )}
                                                                                    </VStack>
                                                                                </Td>
                                                                                <Td px={2} py={3}>
                                                                                    <Text fontSize="14px" color="#000" fontWeight="400">
                                                                                        {payment.scheduledDate 
                                                                                            ? new Date(`${payment.scheduledDate}T00:00:00`).toLocaleDateString("pt-BR")
                                                                                            : "—"}
                                                                                    </Text>
                                                                                </Td>
                                                                                <Td px={2} py={3} isNumeric>
                                                                                    <Text fontSize="14px" color="#000" fontWeight="400">
                                                                                        {formatCurrency({ value: payment.amount || 0 })}
                                                                                    </Text>
                                                                                </Td>
                                                                            </Tr>
                                                                        );
                                                                    })}
                                                            </Tbody>
                                                        </Table>

                                                        {/* Total */}
                                                        <Flex justify="space-between" align="center" w="100%" mt={4} pt={4} borderTop="1px solid #E6E9F0">
                                                            <Text fontSize="16px" color="#000" fontWeight="600">
                                                                Total
                                                            </Text>
                                                            <Text fontSize="16px" color="#000" fontWeight="600">
                                                                {formatCurrency({ 
                                                                    value: batchResponse.payments
                                                                        ?.filter((p: any) => p.status !== "FAILED")
                                                                        ?.reduce((sum: number, p: any) => sum + (p.amount || 0), 0) || 0
                                                                })}
                                                            </Text>
                                                        </Flex>
                                                    </Box>
                                                )}
                                            </VStack>
                                        </VStack>
                                    </Flex>
                                )}
                            </Flex>
                        )}

                        {section === "ERROR" && (
                            <Flex
                                w="100%"
                                h="100%"
                                align="center"
                                justify="center"
                                flexDir="column"
                                gap="4"
                            >
                                <CloseCircleIcon />
                                <Text
                                    maxW="268px"
                                    w="100%"
                                    fontSize="20px"
                                    textAlign="center"
                                >
                                    Houve um erro ao processar o pagamento.
                                </Text>
                                <Text fontSize="14px">
                                    Tente novamente
                                </Text>
                            </Flex>
                        )}

                        {section === "SUCCESS" && (
                            <Flex
                                w="100%"
                                h="100%"
                                align="center"
                                justify="center"
                                flexDir="column"
                                gap="4"
                                p={4}
                            >
                                <CheckCircleIcon2 />
                                <Text
                                    maxW="268px"
                                    w="100%"
                                    fontSize="20px"
                                    textAlign="center"
                                >
                                    Pagamentos processados com sucesso!
                                </Text>
                                <Text
                                    maxW="268px"
                                    w="100%"
                                    fontSize="16px"
                                    textAlign="center"
                                >
                                    Você pode acompanhar o status dos pagamentos no menu de pagamentos, na aba de aprovação.
                                </Text>
                            </Flex>
                        )}
                    </Flex>
        </Drawer>
    );
}
